import { useState, useCallback, useEffect } from 'react';
import type { WeekSlotSuggestion, WeekSlotGeneration, WeekPlanPhase, WeekSlidePreview } from '../../types';
import { confirmDialog } from '../ui/dialog';
import { t } from '../../i18n/translations';
import { contentStudioApi } from '../../services/contentStudioApi';
import type { ImageTemplate } from '../../types';

const THEME_COLORS: Record<string, string> = {
  tech: 'bg-accent/10 border-accent/30 text-accent',
  consulting: 'bg-violet-500/10 border-violet-500/30 text-violet-300',
  agency: 'bg-orange-500/10 border-orange-500/30 text-orange-300',
  bold: 'bg-red-500/10 border-red-500/30 text-red-300',
  clean: 'bg-slate-500/10 border-slate-500/30 text-slate-300',
  warm: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
  elegant: 'bg-pink-500/10 border-pink-500/30 text-pink-300',
};

function TemplatePicker({
  currentTemplateId,
  contentFormat,
  onSelect,
  onClose,
}: {
  currentTemplateId: string | null;
  contentFormat?: string;
  onSelect: (templateId: string) => void;
  onClose: () => void;
}) {
  const [templates, setTemplates] = useState<ImageTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contentStudioApi.listTemplates().then((all) => {
      const filtered = contentFormat
        ? all.filter((t) => t.content_format === contentFormat)
        : all;
      setTemplates(filtered.length > 0 ? filtered : all);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [contentFormat]);

  return (
    <div className="mt-1 bg-bg-main border border-violet-500/20 rounded p-1.5 space-y-1">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[8px] font-semibold text-violet-400 uppercase tracking-wide">Template wählen</span>
        <button onClick={onClose} className="text-[8px] text-text-muted hover:text-text-main">✕</button>
      </div>
      {loading ? (
        <span className="text-[8px] text-text-muted">Laden...</span>
      ) : (
        <div className="flex flex-wrap gap-0.5 max-h-24 overflow-y-auto">
          {templates.map((tpl) => {
            const colorClass = THEME_COLORS[tpl.theme_family ?? ''] ?? 'bg-slate-700/50 border-slate-600 text-text-muted';
            const isActive = tpl.id === currentTemplateId;
            return (
              <button
                key={tpl.id}
                onClick={() => onSelect(tpl.id)}
                title={tpl.id}
                className={`text-[8px] px-1 py-0.5 rounded border transition-colors ${
                  isActive
                    ? 'bg-violet-500/30 border-violet-400 text-violet-200 font-semibold'
                    : `${colorClass} hover:border-violet-400/50`
                }`}
              >
                {tpl.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function extractPreviewText(text: string): string {
  const stripped = text.replace(/^```[a-zA-Z]*\s*\n?/, '').replace(/\n?```\s*$/, '').trim();
  try {
    const j = JSON.parse(stripped);
    if (j.slides && Array.isArray(j.slides) && j.slides.length > 0) return j.slides[0].text || j.slides[0].slide_text || '';
    if (j.caption) return j.caption;
    if (j.text) return j.text;
    if (j.hook) return j.hook;
  } catch {
    // not JSON, use as-is
  }
  return stripped;
}

const DAYS: string[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const TIME_WINDOWS: string[] = ['morning', 'afternoon', 'evening'];
const TIME_LABELS: Record<string, string> = {
  morning: 'AM',
  afternoon: 'PM',
  evening: 'EV',
};

const REJECTION_PRESETS = [
  'Zu generisch',
  'Falscher Ton',
  'Thema verfehlt',
  'Zu lang',
  'Falsche Zielgruppe',
  'Fakten falsch',
];

// Returns true if the given day-of-week is strictly before today
export function isDayPast(day: string, mondayIso?: string | null): boolean {
  const dayIndex = DAYS.indexOf(day);
  if (dayIndex === -1) return false;
  if (mondayIso) {
    const monday = new Date(mondayIso + 'T00:00:00');
    const slotDate = new Date(monday);
    slotDate.setDate(monday.getDate() + dayIndex);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return slotDate < today;
  }
  // Fallback: relative day comparison
  const jsDay = new Date().getDay();
  const todayIndex = jsDay === 0 ? 6 : jsDay - 1;
  return dayIndex < todayIndex;
}

function getDayDate(dayIndex: number, mondayIso?: string | null): string {
  if (!mondayIso) return '';
  const monday = new Date(mondayIso + 'T00:00:00');
  const d = new Date(monday);
  d.setDate(monday.getDate() + dayIndex);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
}

interface Props {
  lang: string;
  suggestions: WeekSlotSuggestion[];
  generations: WeekSlotGeneration[];
  phase: WeekPlanPhase;
  progress: Record<number, string>;
  onUpdateSuggestion: (slotId: number, patch: Partial<WeekSlotSuggestion>) => void;
  onRemoveSuggestion: (slotId: number) => void;
  onRegenerate: (slotId: number) => void;
  /** @deprecated replaced by onRejectText panel · kept for caller compat */
  onRegenerateEdit?: (slotId: number) => void;
  onGenerateSingle: (slotId: number) => void;
  onSwapTemplate: (generationId: number) => void;
  onSelectTemplate: (generationId: number, templateId: string) => void;
  onRejectText: (slotId: number, data: { presets: string[]; reason?: string }) => void;
  onCancelSchedule: (publishLogId: number) => void;
  onDeleteGeneration: (generationId: number) => void;
  onPublishSlot: (generationId: number) => void;
  publishingSlotIds: Set<number>;
  weekRefreshing?: boolean;
  weekMonday?: string | null;
  timeWindowMap?: Record<string, string>;
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'generating') {
    return (
      <span className="inline-block w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    );
  }
  if (status === 'done') {
    return <span className="text-green-400 text-sm font-bold">&#10003;</span>;
  }
  if (status === 'error') {
    return <span className="text-red-400 text-sm font-bold">!</span>;
  }
  return null;
}

export default function WeekPlanGrid({
  lang,
  suggestions,
  generations,
  phase,
  progress,
  onUpdateSuggestion,
  onRemoveSuggestion,
  onRegenerate,
  onGenerateSingle,
  onSwapTemplate,
  onSelectTemplate,
  onRejectText,
  onCancelSchedule,
  onDeleteGeneration,
  onPublishSlot,
  publishingSlotIds,
  weekRefreshing,
  weekMonday,
  timeWindowMap,
}: Props) {
  // Track which slots have stale previews (user edited topic/tension/hook after gen was done)
  const [staleSlots, setStaleSlots] = useState<Set<number>>(new Set());
  // Track which slot has rejection panel open
  const [rejectionOpenSlotId, setRejectionOpenSlotId] = useState<number | null>(null);
  // Track selected rejection presets per slot
  const [selectedPresets, setSelectedPresets] = useState<Record<number, string[]>>({});
  // Track free-text rejection reason per slot
  const [rejectionReasons, setRejectionReasons] = useState<Record<number, string>>({});
  // Lightbox: full carousel preview
  const [lightboxSlides, setLightboxSlides] = useState<WeekSlidePreview[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  // Template picker: which generationId has picker open
  const [templatePickerGenId, setTemplatePickerGenId] = useState<number | null>(null);

  const openLightbox = useCallback((slides: WeekSlidePreview[], startIndex = 0) => {
    const withImages = slides.filter(s => s.image_path);
    if (withImages.length > 0) {
      setLightboxSlides(withImages);
      setLightboxIndex(Math.min(startIndex, withImages.length - 1));
    }
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxSlides(null);
    setLightboxIndex(0);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxSlides) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') setLightboxIndex(i => Math.min(i + 1, lightboxSlides.length - 1));
      if (e.key === 'ArrowLeft') setLightboxIndex(i => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxSlides, closeLightbox]);

  const getSuggestion = (day: string, timeWindow: string) =>
    suggestions.find((s) => s.day === day && s.time_window === timeWindow);

  const getGeneration = (day: string, timeWindow: string) => {
    const sg = getSuggestion(day, timeWindow);
    if (!sg) return null;
    return generations.find((g) => g.slot_id === sg.slot_id) ?? null;
  };

  const handleSuggestionEdit = (slotId: number, patch: Partial<WeekSlotSuggestion>) => {
    onUpdateSuggestion(slotId, patch);
    // If topic/tension/hook changed and slot has a done gen, mark as stale
    const gen = generations.find((g) => g.slot_id === slotId);
    if (gen && gen.status === 'done' && ('topic_angle' in patch || 'tension_category' in patch || 'hook_formula' in patch)) {
      setStaleSlots((prev) => new Set(prev).add(slotId));
    }
  };

  const handleGenSingle = (slotId: number) => {
    setStaleSlots((prev) => {
      const next = new Set(prev);
      next.delete(slotId);
      return next;
    });
    onGenerateSingle(slotId);
  };

  const togglePreset = (slotId: number, preset: string) => {
    setSelectedPresets((prev) => {
      const current = prev[slotId] ?? [];
      const next = current.includes(preset)
        ? current.filter((p) => p !== preset)
        : [...current, preset];
      return { ...prev, [slotId]: next };
    });
  };

  const handleRejectAndRegen = (slotId: number) => {
    const presets = selectedPresets[slotId] ?? [];
    const reason = rejectionReasons[slotId] ?? '';
    onRejectText(slotId, { presets, reason: reason || undefined });
    // Reset rejection state
    setRejectionOpenSlotId(null);
    setSelectedPresets((prev) => ({ ...prev, [slotId]: [] }));
    setRejectionReasons((prev) => ({ ...prev, [slotId]: '' }));
  };

  const visibleDays = DAYS;
  const gridCols = `48px repeat(${visibleDays.length}, 1fr)`;

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[400px]">
        {/* Column headers */}
        <div className="grid gap-1 mb-1" style={{ gridTemplateColumns: gridCols }}>
          <div /> {/* Row label column */}
          {visibleDays.map((day) => {
            const dayIdx = DAYS.indexOf(day);
            const dateStr = getDayDate(dayIdx, weekMonday);
            return (
              <div
                key={day}
                className="text-center text-xs font-semibold py-1 rounded text-text-muted"
              >
                <div>{t(`plan.day.${day}`, lang).slice(0, 2).toUpperCase()}</div>
                {dateStr && <div className="text-[10px] font-normal opacity-70">{dateStr}</div>}
              </div>
            );
          })}
        </div>

        {/* Rows */}
        {TIME_WINDOWS.map((tw) => (
          <div key={tw} className="grid gap-1 mb-1" style={{ gridTemplateColumns: gridCols }}>
            {/* Row label */}
            <div className="flex items-center justify-center">
              <span className="text-xs font-medium text-text-muted">{timeWindowMap?.[tw] || TIME_LABELS[tw]}</span>
            </div>

            {/* Cells */}
            {visibleDays.map((day) => {
              const sg = getSuggestion(day, tw);
              const gen = getGeneration(day, tw);
              const isPast = isDayPast(day, weekMonday);
              const hasDoneGen = gen?.status === 'done';
              const isStale = sg ? staleSlots.has(sg.slot_id) : false;
              const isRejectionOpen = sg ? rejectionOpenSlotId === sg.slot_id : false;
              const slotPresets = sg ? (selectedPresets[sg.slot_id] ?? []) : [];
              const slotReason = sg ? (rejectionReasons[sg.slot_id] ?? '') : '';

              return (
                <div
                  key={day}
                  className={`relative min-h-[180px] rounded-lg border transition-all ${
                    isPast ? 'opacity-50' : ''
                  } ${
                    sg && !sg.skipped
                      ? 'bg-bg-surface border-slate-700'
                      : 'bg-bg-main border-dashed border-slate-700/50'
                  }`}
                >
                  {/* idle phase */}
                  {phase === 'idle' && sg && !sg.skipped && (
                    <div className="p-1.5 space-y-1">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[10px] font-medium bg-slate-700 text-text-main px-1.5 py-0.5 rounded truncate max-w-[70%]">
                          {sg.content_format}
                        </span>
                        <button
                          onClick={() => onRemoveSuggestion(sg.slot_id)}
                          className="text-red-400/60 hover:text-red-400 text-xs leading-none transition-colors shrink-0"
                          title="Remove slot"
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  )}

                  {/* review phase */}
                  {phase === 'review' && sg && !sg.skipped && (
                    <div className="p-1.5 space-y-1">
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[10px] font-medium bg-primary/20 text-primary px-1.5 py-0.5 rounded truncate max-w-[70%]">
                          {sg.content_format}
                        </span>
                        <button
                          onClick={() => onRemoveSuggestion(sg.slot_id)}
                          className="text-red-400/60 hover:text-red-400 text-xs leading-none transition-colors shrink-0"
                          title="Skip slot"
                        >
                          &times;
                        </button>
                      </div>
                      <textarea
                        value={sg.topic_angle}
                        onChange={(e) => handleSuggestionEdit(sg.slot_id, { topic_angle: e.target.value })}
                        rows={2}
                        className="w-full bg-bg-main border border-slate-700 rounded text-[10px] text-text-main px-1.5 py-1 focus:outline-none focus:border-primary resize-none"
                        placeholder="Topic angle..."
                      />
                      {sg.tension_category && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="font-mono text-[9px] bg-amber-400/10 text-amber-400 border border-amber-400/20 px-1 py-0.5 rounded">{sg.tension_category}</span>
                          <span className="text-[9px] text-text-muted">×</span>
                          <span className="font-mono text-[9px] bg-accent/10 text-accent border border-accent/20 px-1 py-0.5 rounded">{sg.hook_formula}</span>
                          {sg.changed_variable && (
                            <span className="text-[9px] text-primary ml-1">← {sg.changed_variable}</span>
                          )}
                        </div>
                      )}
                      {sg.reasoning && (
                        <p className="text-[9px] text-text-muted mt-0.5 italic">{sg.reasoning}</p>
                      )}

                      {/* Done-gen preview with staleness indicator */}
                      {hasDoneGen && gen && (
                        <div className={`mt-1 rounded border p-1 ${isStale ? 'border-amber-500/40 bg-amber-500/5' : 'border-green-500/30 bg-green-500/5'}`}>
                          {isStale && (
                            <span className="text-[8px] font-semibold text-amber-400 bg-amber-400/10 px-1 py-0.5 rounded mb-0.5 inline-block">
                              Veraltet
                            </span>
                          )}
                          {gen.slide1_image_path ? (
                            <img src={`/api/content-studio/images/${gen.slide1_image_path}${gen._imageTs ? `?t=${gen._imageTs}` : ''}`} alt="Slide 1" className="w-full rounded max-h-32 object-contain cursor-pointer hover:opacity-80" onClick={() => openLightbox(gen.slides ?? [{ slide_number: 1, slide_text: '', image_path: gen.slide1_image_path }])} />
                          ) : (
                            <p className="text-[11px] text-text-muted line-clamp-3 leading-snug">
                              {gen.slide1_text || extractPreviewText(gen.display_text || gen.output_text || '').slice(0, 120)}
                            </p>
                          )}
                        </div>
                      )}

                      {/* [Gen] button · only if no done generation for this slot */}
                      {!hasDoneGen && (
                        <button
                          onClick={() => handleGenSingle(sg.slot_id)}
                          disabled={gen?.status === 'generating'}
                          className="w-full mt-0.5 text-[9px] font-medium bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 rounded px-1 py-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {gen?.status === 'generating' ? (
                            <span className="flex items-center justify-center gap-1">
                              <span className="inline-block w-2.5 h-2.5 border-[1.5px] border-primary border-t-transparent rounded-full animate-spin" />
                              Gen...
                            </span>
                          ) : '[Gen]'}
                        </button>
                      )}

                      {/* If done gen: [Gen] button replaced by re-gen (to force re-gen even with done) */}
                      {hasDoneGen && gen && gen.generation_id && (
                        <button
                          onClick={() => handleGenSingle(sg.slot_id)}
                          disabled={gen.status === 'generating'}
                          className="w-full mt-0.5 text-[9px] font-medium bg-slate-700/50 hover:bg-slate-700 text-text-muted border border-slate-700 rounded px-1 py-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {gen.status === 'generating' ? (
                            <span className="flex items-center justify-center gap-1">
                              <span className="inline-block w-2.5 h-2.5 border-[1.5px] border-text-muted border-t-transparent rounded-full animate-spin" />
                              Gen...
                            </span>
                          ) : '[Gen]'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* review phase: skipped slot */}
                  {phase === 'review' && sg && sg.skipped && (
                    <div className="p-1.5 flex items-center justify-center h-full">
                      <button
                        onClick={() => onUpdateSuggestion(sg.slot_id, { skipped: false })}
                        className="text-[10px] text-text-muted hover:text-primary transition-colors"
                      >
                        + Add
                      </button>
                    </div>
                  )}

                  {/* generating phase */}
                  {phase === 'generating' && sg && !sg.skipped && (
                    <div className="p-1.5 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-medium bg-slate-700 text-text-main px-1.5 py-0.5 rounded truncate max-w-[75%]">
                          {sg.content_format}
                        </span>
                        {progress[sg.slot_id] && (
                          <StatusIcon status={progress[sg.slot_id]} />
                        )}
                      </div>
                      {(!progress[sg.slot_id] || progress[sg.slot_id] === 'generating') && (
                        <div className="flex items-center justify-center py-2">
                          <span className="inline-block w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* done phase */}
                  {phase === 'done' && sg && !sg.skipped && (
                    <>
                      {/* done phase: show output */}
                      {gen && gen.status === 'done' && (
                        <div className="p-1.5 space-y-1">
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-[10px] font-medium bg-slate-700 text-text-main px-1.5 py-0.5 rounded truncate max-w-[60%]">
                              {sg.content_format}
                            </span>
                            {/* Schedule status badge */}
                            {gen.is_scheduled && gen.schedule_status === 'scheduled' && (
                              <span className="text-[9px] font-semibold bg-green-500/20 text-green-400 border border-green-500/30 px-1 py-0.5 rounded">Geplant</span>
                            )}
                            {gen.is_scheduled && gen.schedule_status === 'publishing' && (
                              <span className="text-[9px] font-semibold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-1 py-0.5 rounded">Wird veröffentlicht</span>
                            )}
                            {gen.is_scheduled && gen.schedule_status === 'published' && (
                              <span className="text-[9px] font-semibold bg-slate-600/50 text-text-muted border border-slate-600 px-1 py-0.5 rounded">Veröffentlicht</span>
                            )}
                            {!gen.is_scheduled && (
                              <span className="text-[9px] text-green-400">done</span>
                            )}
                          </div>
                          {gen.slide1_image_path ? (
                            <img
                              src={`/api/content-studio/images/${gen.slide1_image_path}${gen._imageTs ? `?t=${gen._imageTs}` : ''}`}
                              alt="Slide 1"
                              title={gen.template_id ?? undefined}
                              className="w-full rounded max-h-40 object-contain cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => openLightbox(gen.slides ?? [{ slide_number: 1, slide_text: '', image_path: gen.slide1_image_path }])}
                            />
                          ) : (
                            <div title={gen.template_id ?? undefined}>
                              <p className="text-xs text-text-main line-clamp-4 leading-snug font-medium">
                                {gen.slide1_text || (() => { const clean = extractPreviewText(gen.display_text || gen.output_text || ''); return clean.slice(0, 150) + (clean.length > 150 ? '...' : ''); })()}
                              </p>
                            </div>
                          )}

                          {/* Action buttons · vary by schedule state */}
                          {/* Case: scheduled · Jetzt posten + Stornieren */}
                          {gen.is_scheduled && gen.schedule_status === 'scheduled' && (
                            <div className="flex flex-col gap-1">
                              {gen.generation_id != null && (
                                <button
                                  onClick={() => onPublishSlot(gen.generation_id as number)}
                                  disabled={publishingSlotIds.has(gen.generation_id as number)}
                                  className="w-full text-[9px] font-medium bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/30 rounded px-1 py-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                                >
                                  {publishingSlotIds.has(gen.generation_id as number) ? (
                                    <>
                                      <span className="inline-block w-2.5 h-2.5 border-[1.5px] border-purple-300 border-t-transparent rounded-full animate-spin" />
                                      Wird gepostet...
                                    </>
                                  ) : 'Jetzt posten'}
                                </button>
                              )}
                              <button
                                onClick={() => onCancelSchedule(gen.publish_log_id!)}
                                disabled={progress[gen.generation_id!] === 'canceling'}
                                className="text-[9px] text-red-400/70 hover:text-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed self-start"
                              >
                                {progress[gen.generation_id!] === 'canceling' ? (
                                  <span className="flex items-center gap-1">
                                    <span className="inline-block w-2.5 h-2.5 border-[1.5px] border-red-400 border-t-transparent rounded-full animate-spin" />
                                    Stornieren...
                                  </span>
                                ) : 'Stornieren'}
                              </button>
                            </div>
                          )}

                          {/* Case: publishing or published · read-only, no buttons */}

                          {/* Case: not scheduled · normal action buttons */}
                          {!gen.is_scheduled && (
                            <div className="flex flex-col gap-0.5">
                              {gen.generation_id != null && (
                                <button
                                  onClick={() => onPublishSlot(gen.generation_id as number)}
                                  disabled={publishingSlotIds.has(gen.generation_id as number)}
                                  className="w-full text-[9px] font-medium bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/30 rounded px-1 py-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                                >
                                  {publishingSlotIds.has(gen.generation_id as number) ? (
                                    <>
                                      <span className="inline-block w-2.5 h-2.5 border-[1.5px] border-purple-300 border-t-transparent rounded-full animate-spin" />
                                      Wird gepostet...
                                    </>
                                  ) : 'Jetzt posten'}
                                </button>
                              )}
                              <div className="flex gap-1 flex-wrap">
                                <button
                                  onClick={() => onRegenerate(gen.slot_id)}
                                  className="text-[9px] text-primary hover:text-primary/80 transition-colors"
                                >
                                  Text neu generieren
                                </button>
                                {gen.generation_id != null && (
                                  <>
                                    <span className="text-text-muted/40 text-[9px]">·</span>
                                    <button
                                      onClick={() => onSwapTemplate(gen.generation_id as number)}
                                      className="text-[9px] text-accent hover:text-accent-hover transition-colors"
                                    >
                                      Zufall
                                    </button>
                                    <span className="text-text-muted/40 text-[9px]">·</span>
                                    <button
                                      onClick={() => setTemplatePickerGenId(
                                        templatePickerGenId === gen.generation_id ? null : (gen.generation_id as number)
                                      )}
                                      className="text-[9px] text-violet-400 hover:text-violet-300 transition-colors"
                                    >
                                      Template wählen
                                    </button>
                                    <span className="text-text-muted/40 text-[9px]">·</span>
                                    <button
                                      onClick={() => {
                                        setRejectionOpenSlotId((prev) => prev === gen.slot_id ? null : gen.slot_id);
                                      }}
                                      className="text-[9px] text-red-400/70 hover:text-red-400 transition-colors"
                                    >
                                      &#10005; Ablehnen
                                    </button>
                                    <span className="text-text-muted/40 text-[9px]">·</span>
                                    <button
                                      onClick={async () => {
                                        if (!(await confirmDialog({ message: 'Diesen Inhalt wirklich entfernen?', tone: 'danger' }))) return;
                                        onDeleteGeneration(gen.generation_id as number);
                                      }}
                                      disabled={progress[gen.generation_id] === 'deleting'}
                                      className="text-[9px] text-red-500/70 hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Entfernen"
                                    >
                                      {progress[gen.generation_id] === 'deleting' ? (
                                        <span className="flex items-center gap-1">
                                          <span className="inline-block w-2.5 h-2.5 border-[1.5px] border-red-500 border-t-transparent rounded-full animate-spin" />
                                        </span>
                                      ) : '✕ Entfernen'}
                                    </button>
                                  </>
                                )}
                              </div>

                              {/* Template picker panel */}
                              {templatePickerGenId === gen.generation_id && gen.generation_id != null && (
                                <TemplatePicker
                                  currentTemplateId={gen.template_id}
                                  contentFormat={gen.content_format}
                                  onSelect={(templateId) => {
                                    onSelectTemplate(gen.generation_id as number, templateId);
                                    setTemplatePickerGenId(null);
                                  }}
                                  onClose={() => setTemplatePickerGenId(null)}
                                />
                              )}

                              {/* Rejection panel */}
                              {isRejectionOpen && gen.generation_id != null && (
                                <div className="mt-1 bg-bg-main border border-red-500/20 rounded p-1.5 space-y-1">
                                  <div className="flex flex-wrap gap-0.5">
                                    {REJECTION_PRESETS.map((preset) => (
                                      <button
                                        key={preset}
                                        onClick={() => togglePreset(gen.slot_id, preset)}
                                        className={`text-[8px] px-1 py-0.5 rounded border transition-colors ${
                                          slotPresets.includes(preset)
                                            ? 'bg-red-500/20 border-red-500/50 text-red-300'
                                            : 'bg-slate-700/50 border-slate-600 text-text-muted hover:border-red-500/30 hover:text-red-400/70'
                                        }`}
                                      >
                                        {preset}
                                      </button>
                                    ))}
                                  </div>
                                  <textarea
                                    value={slotReason}
                                    onChange={(e) => setRejectionReasons((prev) => ({ ...prev, [gen.slot_id]: e.target.value }))}
                                    rows={2}
                                    placeholder="Weitere Details..."
                                    className="w-full bg-bg-surface border border-slate-700 rounded text-[9px] text-text-main px-1.5 py-1 focus:outline-none focus:border-red-500/50 resize-none"
                                  />
                                  <button
                                    onClick={() => handleRejectAndRegen(gen.slot_id)}
                                    disabled={slotPresets.length === 0 && !slotReason.trim()}
                                    className="w-full text-[9px] font-medium bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded px-1 py-0.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    Ablehnen &amp; neu generieren
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* done phase: generating (single slot or regen in progress) */}
                      {gen && gen.status === 'generating' && (
                        <div className="p-1.5 space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-medium bg-slate-700 text-text-main px-1.5 py-0.5 rounded truncate max-w-[75%]">
                              {sg.content_format}
                            </span>
                            <span className="inline-block w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          </div>
                        </div>
                      )}

                      {/* done phase: error state */}
                      {gen && gen.status === 'error' && (
                        <div className="p-1.5 space-y-1">
                          <div className="flex items-center gap-1">
                            <span className="text-red-400 text-xs">!</span>
                            <span className="text-[10px] text-red-400/80 truncate">
                              {gen.error ?? 'Error'}
                            </span>
                          </div>
                          <button
                            onClick={() => onRegenerate(gen.slot_id)}
                            className="text-[9px] text-primary hover:text-primary/80 transition-colors"
                          >
                            Retry
                          </button>
                        </div>
                      )}

                      {/* done phase: slot emptied · offer re-creation */}
                      {!gen && (
                        <div className="p-1.5 flex items-center justify-center h-full">
                          <button
                            onClick={() => handleGenSingle(sg.slot_id)}
                            disabled={weekRefreshing}
                            className="text-[10px] text-primary hover:text-primary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            + Inhalt erstellen
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {/* Empty cell placeholder */}
                  {!sg && phase === 'idle' && (
                    <div className="flex items-center justify-center h-full p-2">
                      <span className="text-[10px] text-text-muted/30">-</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Carousel Lightbox */}
      {lightboxSlides && lightboxSlides.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          <div className="relative flex items-center gap-4" onClick={e => e.stopPropagation()}>
            {/* Left arrow */}
            {lightboxIndex > 0 && (
              <button
                onClick={() => setLightboxIndex(i => i - 1)}
                className="text-white/70 hover:text-white text-4xl font-light w-12 h-12 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors shrink-0"
              >
                &#8249;
              </button>
            )}
            {lightboxIndex === 0 && <div className="w-12 shrink-0" />}

            {/* Slide image */}
            <div className="relative">
              <img
                src={`/api/content-studio/images/${lightboxSlides[lightboxIndex].image_path}`}
                alt={`Slide ${lightboxSlides[lightboxIndex].slide_number}`}
                className="max-w-[80vw] max-h-[85vh] object-contain rounded-lg"
              />
              {/* Counter */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                {lightboxIndex + 1} / {lightboxSlides.length}
              </div>
              {/* Slide role badge */}
              {lightboxSlides[lightboxIndex].slide_role && (
                <div className="absolute top-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  {lightboxSlides[lightboxIndex].slide_role}
                </div>
              )}
            </div>

            {/* Right arrow */}
            {lightboxIndex < lightboxSlides.length - 1 && (
              <button
                onClick={() => setLightboxIndex(i => i + 1)}
                className="text-white/70 hover:text-white text-4xl font-light w-12 h-12 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors shrink-0"
              >
                &#8250;
              </button>
            )}
            {lightboxIndex >= lightboxSlides.length - 1 && <div className="w-12 shrink-0" />}
          </div>

          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/70 hover:text-white text-2xl font-bold w-10 h-10 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
          >
            &times;
          </button>

          {/* Dot indicators */}
          {lightboxSlides.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5">
              {lightboxSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  className={`w-2 h-2 rounded-full transition-colors ${i === lightboxIndex ? 'bg-white' : 'bg-white/30 hover:bg-white/50'}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
